import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AdminPageHeader } from '../components/layout/AdminPageHeader';
import { Card, Badge, Button, Spinner } from '../components/ui';
import { useTranslation } from 'react-i18next';
import { adminApi } from '../services/api';
import { toast } from 'sonner';

const AdminCylinders = () => {
  const { t } = useTranslation();
  const [cylinders, setCylinders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    fetchCylinders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCylinders = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getCylinders();
      setCylinders(data || []);
    } catch (err) {
      toast.error(t('adminCylinders.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredCylinders = cylinders.filter(c => 
    (c.serialNumber || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.currentUser?.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedCylinders = filteredCylinders.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filteredCylinders.length / ITEMS_PER_PAGE);

  if (loading) return <div className="h-[60vh] flex items-center justify-center"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-10 pb-10">
      <AdminPageHeader
        tag="INVENTORY"
        title={t('dashboard.cylinderManagement') || 'Cylinder Management'}
        subtitle={`${cylinders.length} ${t('dashboard.totalCylinders') || 'GAS CYLINDERS IN SYSTEM'}`}
        icon="propane_tank"
      >
        <div className="relative group flex-1">
          <input
            type="text"
            placeholder={t('dashboard.searchCylinders') || 'Search serial numbers...'}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="w-full sm:w-80 h-14 bg-white dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-3xl px-12 outline-none focus:border-[#1e9f6b]/50 transition-all font-bold text-sm dark:text-white shadow-xl shadow-slate-900/5"
          />
          <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#1e9f6b] transition-colors">
            search
          </span>
        </div>
        <Button
          variant="ghost"
          onClick={fetchCylinders}
          className="h-14 w-14 rounded-3xl p-0 hover:bg-[#1e9f6b]/10 hover:text-[#1e9f6b] transition-colors"
        >
          <span className="material-symbols-outlined">refresh</span>
        </Button>
      </AdminPageHeader>

      {/* Cylinders Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="overflow-hidden bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-2xl shadow-slate-900/5">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-separate border-spacing-0">
              <thead>
                <tr className="bg-slate-50/50 dark:bg-slate-900/30">
                  <th className="px-10 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.cylinderSerial')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.capacity')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.currentUser')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.lastFilled')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.status')}</th>
                  <th className="px-10 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em] text-right">{t('dashboard.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                <AnimatePresence mode="popLayout">
                  {paginatedCylinders.map((cyl, idx) => (
                    <motion.tr 
                      key={cyl.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      transition={{ delay: idx * 0.05 }}
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all duration-300 group"
                    >
                      <td className="px-10 py-6">
                        <div className="flex items-center gap-4">
                           <div className="h-10 w-10 bg-[#1e9f6b]/10 rounded-xl flex items-center justify-center">
                              <span className="material-symbols-outlined text-[#1e9f6b] text-xl">propane_tank</span>
                           </div>
                           <div>
                              <div className="text-xs font-black italic uppercase text-slate-900 dark:text-white tracking-tight">{cyl.serialNumber}</div>
                              <div className="text-[9px] font-black text-slate-400 uppercase italic tracking-widest mt-0.5">{t('adminCylinders.created')}: {new Date(cyl.createdAt).toLocaleDateString()}</div>
                           </div>
                        </div>
                      </td>

                      <td className="px-6 py-6">
                        <Badge variant="slate" className="rounded-xl font-black italic uppercase text-[8px] tracking-wider px-3 py-1.5 border-0 bg-slate-100 dark:bg-slate-800 text-slate-500">
                          {cyl.capacityLiters} LITR
                        </Badge>
                      </td>

                      <td className="px-6 py-6">
                        {cyl.currentUser ? (
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-lg bg-indigo-500/10 flex items-center justify-center font-black text-[10px] text-indigo-500 uppercase italic">
                              {cyl.currentUser.avatarUrl ? (
                                  <img src={cyl.currentUser.avatarUrl} alt="" className="h-full w-full object-cover rounded-lg" />
                              ) : cyl.currentUser.name?.[0]}
                            </div>
                            <div className="text-xs font-black italic uppercase text-slate-700 dark:text-slate-300">{cyl.currentUser.name}</div>
                          </div>
                        ) : (
                          <span className="text-[10px] font-black text-slate-400 uppercase italic">{t('adminCylinders.inStock')}</span>
                        )}
                      </td>

                      <td className="px-6 py-6 text-xs font-black italic uppercase tracking-tighter text-slate-600 dark:text-slate-400">
                        {cyl.lastFilledAt ? new Date(cyl.lastFilledAt).toLocaleDateString() : '—'}
                      </td>

                      <td className="px-6 py-6">
                        <Badge 
                          variant={
                            cyl.status === 'available' ? 'green' : 
                            cyl.status === 'in_use' ? 'blue' : 
                            'red'
                          }
                          pulse={cyl.status === 'in_use'}
                          className="rounded-xl font-black italic uppercase text-[8px] tracking-wider px-3 py-1.5 border-0"
                        >
                          {cyl.status}
                        </Badge>
                      </td>

                      <td className="px-10 py-6 text-right">
                         <div className="flex justify-end gap-2 opacity-100 transition-opacity">
                            <Button size="xs" variant="ghost" className="h-8 w-8 rounded-lg p-0"><span className="material-symbols-outlined text-sm">history</span></Button>
                         </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-10 py-8 border-t border-slate-50 dark:border-slate-800/50 flex justify-between items-center bg-slate-50/10 dark:bg-slate-800/5">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">
                {t('common.pageOf', { page, total: totalPages })}
              </div>
              <div className="flex gap-3">
                <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="rounded-2xl h-12 px-6 font-black uppercase italic text-[10px] tracking-widest bg-white dark:bg-slate-800 shadow-sm">
                  <span className="material-symbols-outlined text-sm mr-2">arrow_back</span>
                  {t('common.prev')}
                </Button>
                <Button size="sm" variant="ghost" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="rounded-2xl h-12 px-6 font-black uppercase italic text-[10px] tracking-widest bg-white dark:bg-slate-800 shadow-sm">
                  {t('common.next')}
                  <span className="material-symbols-outlined text-sm ml-2">arrow_forward</span>
                </Button>
              </div>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default AdminCylinders;
